import { ActivityRecordType, TaskTypeId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import {
    activityTypeChoicesForRecordType,
    ActivityTypeOption,
    chooseActivityTypeLabel,
    noActivityTypeKey,
} from "components/tasks/activity-type-options";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import React, { useState } from "react";
import type { TouchpointTypeTarget } from "./touchpoint-type-cell";

interface TouchpointEditorProps {
    target: TouchpointTypeTarget;
    recordType: ActivityRecordType | null;
    activityTypes: ActivityTypeOption[];
    onSaved: () => Promise<void>;
    onClose: () => void;
}

export const TouchpointEditor: React.FC<TouchpointEditorProps> = ({
    target,
    recordType,
    activityTypes,
    onSaved,
    onClose,
}) => {
    const trpcClient = trpc.useTRPC();
    const [taskTypeId, setTaskTypeId] = useState<TaskTypeId | null>(target.taskTypeId);
    const [note, setNote] = useState(target.note ?? "");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const update = useMutation({
        ...trpcClient.touchpoint.update.mutationOptions(),
        onSuccess: async () => {
            await onSaved();
            onClose();
        },
        onError: error =>
            setErrorMessage(error.message || "We could not save this activity."),
    });

    const choices = activityTypeChoicesForRecordType(
        activityTypes,
        target.category,
        recordType,
        taskTypeId,
    );

    const chooseType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTaskTypeId(
            e.target.value === noActivityTypeKey ? null : (e.target.value as TaskTypeId),
        );
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        update.mutate({
            touchpointId: target.touchpointId,
            activityTypeId: taskTypeId,
            note: note.trim() ? note.trim() : null,
        });
    };

    return (
        <ResponsivePanel
            isOpen
            onDismiss={onClose}
            type={PanelType.medium}
            headerText="Edit activity">
            <form onSubmit={submit} className="space-y-4">
                {choices.length > 0 ? (
                    <Select
                        label="Activity type"
                        value={taskTypeId ?? noActivityTypeKey}
                        onChange={chooseType}>
                        <option value={noActivityTypeKey}>
                            {chooseActivityTypeLabel}
                        </option>
                        {choices.map(choice => (
                            <option key={choice.id} value={choice.id}>
                                {choice.name}
                            </option>
                        ))}
                    </Select>
                ) : null}
                <TextArea
                    label="Note (optional)"
                    value={note}
                    placeholder="Add a note about this activity"
                    onChange={e => setNote(e.target.value)}
                />
                {errorMessage ? (
                    <div className="text-red-600 text-sm">{errorMessage}</div>
                ) : null}
                <div className="flex items-center justify-end gap-2">
                    {update.isPending ? <Spinner /> : null}
                    <SecondaryButton
                        type="button"
                        onClick={onClose}
                        disabled={update.isPending}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={update.isPending}>
                        Save
                    </PrimaryButton>
                </div>
            </form>
        </ResponsivePanel>
    );
};
